import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  BookOpen, Shield, Gauge, Truck, Layers, Container, AlertTriangle, 
  Users, Bus, ClipboardCheck, ChevronRight, LogOut, User, Settings,
  Moon, Sun, Palette, Play, RotateCcw, Check, X, Clock, Award,
  BarChart3, BookMarked, GraduationCap, Home, Menu, ChevronLeft,
  Flame, Leaf
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icons mapping
const iconMap = {
  BookOpen, Shield, Gauge, Truck, Layers, Container, 
  AlertTriangle, Users, Bus, ClipboardCheck
};

// Theme context
const ThemeContext = createContext();

const themes = [
  { id: "dark", name: "Night Haul", icon: Moon, description: "Default dark theme" },
  { id: "light", name: "Daylight", icon: Sun, description: "Light theme" },
  { id: "sunset", name: "Sunset Highway", icon: Flame, description: "Warm orange tones" },
  { id: "midnight", name: "Midnight Blue", icon: Moon, description: "Cool blue tones" },
  { id: "forest", name: "Forest Green", icon: Leaf, description: "Nature inspired" },
];

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  useEffect(() => {
    const root = document.documentElement;
    root.className = theme === "dark" ? "" : theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
}

const useTheme = () => useContext(ThemeContext);

// Auth context
const AuthContext = createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

const useAuth = () => useContext(AuthContext);

// Protected Route
function ProtectedRoute({ children }) {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-xl">Loading...</div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Landing Page - Simple Login
function LandingPage() {
  const navigate = useNavigate();
  const { token } = useAuth();

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/login" replace />;
}

// Auth Pages
function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, token } = useAuth();
  const navigate = useNavigate();

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="w-14 h-14 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold">Gideon Learning</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Mesa Winds Intelligence, LLC — A Holcomb Company
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Sign In</CardTitle>
            <CardDescription>Enter your credentials to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="login-password-input"
                />
              </div>
              <Button type="submit" className="w-full h-11 btn-scale" disabled={loading} data-testid="login-submit-btn">
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/register" className="text-primary hover:underline">
                Create one
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}

function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const { register, token } = useAuth();
  const navigate = useNavigate();

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, firstName, lastName);
      toast.success("Welcome to Gideon Learning!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="w-14 h-14 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold">Gideon Learning</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Mesa Winds Intelligence, LLC — A Holcomb Company
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create Account</CardTitle>
            <CardDescription>Get started with your learning journey</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="h-11"
                    data-testid="register-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="h-11"
                    data-testid="register-lastname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-11"
                  data-testid="register-password-input"
                />
              </div>
              <Button type="submit" className="w-full h-11 btn-scale" disabled={loading} data-testid="register-submit-btn">
                {loading ? "Creating account..." : "Create Account"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}

// Main Layout with Sidebar
function MainLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme, themes: themeOptions } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = window.location.pathname;
  const isInCourse = location.includes('/course/') || location.includes('/modules') || location.includes('/flashcards') || location.includes('/tests') || location.includes('/exam') || location.includes('/progress');
  const isAdmin = user?.is_admin;

  let navItems = isInCourse ? [
    { icon: Home, label: "My Courses", path: "/dashboard" },
    { icon: Truck, label: "Course Home", path: "/course/texas-cdl" },
    { icon: BookOpen, label: "Modules", path: "/modules" },
    { icon: BookMarked, label: "Flashcards", path: "/flashcards" },
    { icon: ClipboardCheck, label: "Practice Tests", path: "/tests" },
    { icon: GraduationCap, label: "Exam Simulation", path: "/exam" },
    { icon: BarChart3, label: "Progress", path: "/progress" },
  ] : [
    { icon: Home, label: "My Courses", path: "/dashboard" },
  ];

  // Add admin link only for admins
  if (isAdmin) {
    navItems = [...navItems, { icon: Settings, label: "Admin Panel", path: "/admin" }];
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-lg font-bold">Gideon Learning</h1>
            <p className="text-xs text-muted-foreground">Mesa Winds Intelligence, LLC</p>
          </div>
        </div>
      </div>
      
      <ScrollArea className="flex-1 px-4">
        <nav className="space-y-2">
          {navItems.map((item) => (
            <Button
              key={item.path}
              variant="ghost"
              className="w-full justify-start gap-3 h-11"
              onClick={() => {
                navigate(item.path);
                setSidebarOpen(false);
              }}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Button>
          ))}
        </nav>
      </ScrollArea>

      <div className="p-4 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start gap-3 h-11">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium">{user?.first_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Theme</DropdownMenuLabel>
            {themeOptions.map((t) => (
              <DropdownMenuItem
                key={t.id}
                onClick={() => setTheme(t.id)}
                className={theme === t.id ? "bg-accent" : ""}
              >
                <t.icon className="mr-2 h-4 w-4" />
                {t.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-border flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-border">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-primary flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-bold">Gideon Learning</span>
          </div>
          <div className="w-10" />
        </header>

        <main className="flex-1 overflow-auto">
          {children}
        </main>

        {/* Footer */}
        <footer className="border-t border-border px-4 py-3 text-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Mesa Winds Intelligence, LLC — A Holcomb Company
          </p>
        </footer>
      </div>
    </div>
  );
}

// Dashboard - Generic LMS Course List
function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Enrolled courses - Texas CDL is the first course
  const enrolledCourses = [
    {
      id: "texas-cdl",
      title: "Texas CDL Exam Preparation",
      description: "Complete training for Commercial Driver License certification covering all endorsements and test areas.",
      icon: Truck,
      modules: 10,
      estimatedHours: 15,
      color: "from-amber-500 to-orange-600"
    }
  ];

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="space-y-2">
        <h1 className="text-3xl lg:text-4xl font-bold">
          Welcome, {user?.first_name}!
        </h1>
        <p className="text-muted-foreground">
          Select a course to continue learning
        </p>
      </div>

      {/* Enrolled Courses */}
      <div>
        <h2 className="text-xl font-bold mb-4">Your Courses</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {enrolledCourses.map((course) => (
            <Card 
              key={course.id}
              className="card-hover cursor-pointer overflow-hidden"
              onClick={() => navigate(`/course/${course.id}`)}
              data-testid={`course-card-${course.id}`}
            >
              <div className={`h-2 bg-gradient-to-r ${course.color}`} />
              <CardHeader>
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <course.icon className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="text-xl">{course.title}</CardTitle>
                <CardDescription className="line-clamp-2">
                  {course.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    {course.modules} modules
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    ~{course.estimatedHours} hrs
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full btn-scale">
                  Continue Learning
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </CardFooter>
            </Card>
          ))}

          {/* Placeholder for future courses */}
          <Card className="border-dashed opacity-60">
            <CardHeader>
              <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center mb-4">
                <BookOpen className="w-7 h-7 text-muted-foreground" />
              </div>
              <CardTitle className="text-xl text-muted-foreground">More Courses Coming Soon</CardTitle>
              <CardDescription>
                Additional training programs will be available here
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Course Dashboard - Shows course-specific content
function CourseDashboard() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [recentTests, setRecentTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const fetchCourseData = async () => {
    try {
      const [modulesRes, progressRes, testsRes] = await Promise.all([
        axios.get(`${API}/modules/`),
        axios.get(`${API}/progress/`),
        axios.get(`${API}/tests/results`),
      ]);
      setModules(modulesRes.data);
      setProgress(progressRes.data);
      setRecentTests(testsRes.data.slice(0, 5));
    } catch (error) {
      console.error("Error fetching course data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading course...</div>
      </div>
    );
  }

  const completedSections = Object.values(progress?.sections_completed || {}).flat().length;
  const totalSections = modules.reduce((acc, m) => acc + m.sections.length, 0);
  const progressPercent = totalSections > 0 ? (completedSections / totalSections) * 100 : 0;

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="course-dashboard">
      {/* Back button and Course Header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="mb-4">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to My Courses
        </Button>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Texas CDL Exam Preparation</h1>
            <p className="text-muted-foreground">Commercial Driver License Training</p>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Course Progress</p>
              <p className="text-3xl font-bold text-primary">{progressPercent.toFixed(0)}%</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Sections Completed</p>
              <p className="text-xl font-bold">{completedSections} / {totalSections}</p>
            </div>
          </div>
          <Progress value={progressPercent} className="h-3" />
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Button 
          className="h-24 flex flex-col gap-2 btn-scale"
          onClick={() => navigate("/modules")}
          data-testid="course-modules-btn"
        >
          <BookOpen className="w-7 h-7" />
          <span>Study Modules</span>
        </Button>
        <Button 
          variant="secondary"
          className="h-24 flex flex-col gap-2 btn-scale"
          onClick={() => navigate("/flashcards")}
          data-testid="course-flashcards-btn"
        >
          <BookMarked className="w-7 h-7" />
          <span>Flashcards</span>
        </Button>
        <Button 
          variant="secondary"
          className="h-24 flex flex-col gap-2 btn-scale"
          onClick={() => navigate("/tests")}
          data-testid="course-tests-btn"
        >
          <ClipboardCheck className="w-7 h-7" />
          <span>Practice Tests</span>
        </Button>
        <Button 
          variant="outline"
          className="h-24 flex flex-col gap-2 btn-scale border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          onClick={() => navigate("/exam")}
          data-testid="course-exam-btn"
        >
          <GraduationCap className="w-7 h-7" />
          <span>Full Exam</span>
        </Button>
      </div>

      {/* Modules and Recent Tests Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Modules List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Course Modules</CardTitle>
            <CardDescription>10 modules covering all CDL test areas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {modules.map((module, i) => {
                const IconComponent = iconMap[module.icon] || BookOpen;
                const moduleProgress = progress?.sections_completed?.[module.id]?.length || 0;
                const moduleTotalSections = module.sections.length;
                const modulePercent = (moduleProgress / moduleTotalSections) * 100;

                return (
                  <div 
                    key={module.id}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/modules/${module.id}`)}
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <IconComponent className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{module.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={modulePercent} className="h-1.5 flex-1" />
                        <span className="text-xs text-muted-foreground w-12">
                          {moduleProgress}/{moduleTotalSections}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Tests</CardTitle>
            <CardDescription>Your practice results</CardDescription>
          </CardHeader>
          <CardContent>
            {recentTests.length > 0 ? (
              <div className="space-y-3">
                {recentTests.map((test, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div>
                      <p className="font-medium text-sm capitalize">
                        {test.test_type.replace(/-/g, " ")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(test.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant={test.passed ? "default" : "destructive"}>
                      {test.percentage}%
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No tests taken yet
              </p>
            )}
            <Button 
              variant="ghost" 
              className="w-full mt-4"
              onClick={() => navigate("/progress")}
            >
              View Full Progress
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Modules List Page
function ModulesPage() {
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [modulesRes, progressRes] = await Promise.all([
        axios.get(`${API}/modules/`),
        axios.get(`${API}/progress/`),
      ]);
      setModules(modulesRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      toast.error("Failed to load modules");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading modules...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="modules-page">
      <div>
        <Badge variant="secondary" className="mb-2">Texas CDL Training</Badge>
        <h1 className="text-3xl font-bold">Course Modules</h1>
        <p className="text-muted-foreground mt-2">
          Complete all modules to prepare for your CDL exam
        </p>
      </div>

      <div className="grid gap-4">
        {modules.map((module, i) => {
          const IconComponent = iconMap[module.icon] || BookOpen;
          const completedSections = progress?.sections_completed?.[module.id]?.length || 0;
          const totalSections = module.sections.length;
          const percentComplete = (completedSections / totalSections) * 100;

          return (
            <Card 
              key={module.id}
              className="card-hover cursor-pointer animate-slideUp"
              style={{ animationDelay: `${i * 0.05}s` }}
              onClick={() => navigate(`/modules/${module.id}`)}
              data-testid={`module-card-${module.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <IconComponent className="w-7 h-7 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold">{module.title}</h3>
                        <p className="text-muted-foreground text-sm mt-1 line-clamp-2">
                          {module.description}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                    </div>
                    <div className="flex items-center gap-6 mt-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <BookOpen className="w-4 h-4" />
                        {module.sections.length} sections
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        ~{module.estimated_time} min
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">{percentComplete.toFixed(0)}%</span>
                        </div>
                        <Progress value={percentComplete} className="h-2" />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// Module Detail Page
function ModuleDetailPage() {
  const { moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchModule();
  }, [moduleId]);

  const fetchModule = async () => {
    try {
      const [moduleRes, progressRes] = await Promise.all([
        axios.get(`${API}/modules/${moduleId}`),
        axios.get(`${API}/progress/`),
      ]);
      setModule(moduleRes.data);
      setProgress(progressRes.data);
      if (moduleRes.data.sections.length > 0) {
        setActiveSection(moduleRes.data.sections[0]);
      }
    } catch (error) {
      toast.error("Module not found");
      navigate("/modules");
    } finally {
      setLoading(false);
    }
  };

  // Track module view
  const trackModuleView = async (duration = 0) => {
    try {
      await axios.post(`${API}/analytics/track`, null, {
        params: {
          action: "view_module",
          module_id: moduleId,
          section_id: activeSection?.id,
          duration_seconds: duration
        }
      });
    } catch (error) {
      // Silent fail for tracking
    }
  };

  // Track time spent when leaving
  useEffect(() => {
    const startTime = Date.now();
    return () => {
      const duration = Math.round((Date.now() - startTime) / 1000);
      if (duration > 5) { // Only track if more than 5 seconds
        trackModuleView(duration);
      }
    };
  }, [moduleId, activeSection?.id]);

  const markSectionComplete = async (sectionId) => {
    try {
      await axios.post(`${API}/progress/update`, {
        module_id: moduleId,
        section_id: sectionId,
        completed: true,
      });
      // Track completion
      await axios.post(`${API}/analytics/track`, null, {
        params: { action: "complete_section", module_id: moduleId, section_id: sectionId }
      });
      toast.success("Section completed!");
      fetchModule();
    } catch (error) {
      toast.error("Failed to update progress");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading module...</div>
      </div>
    );
  }

  if (!module) return null;

  const completedSections = progress?.sections_completed?.[moduleId] || [];
  const IconComponent = iconMap[module.icon] || BookOpen;

  return (
    <div className="flex h-full" data-testid="module-detail-page">
      {/* Sidebar */}
      <div className="w-80 border-r border-border flex flex-col bg-card/50 hidden lg:flex">
        <div className="p-6 border-b border-border">
          <Button variant="ghost" size="sm" onClick={() => navigate("/modules")} className="mb-4">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Modules
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <IconComponent className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-bold">{module.title}</h2>
              <p className="text-xs text-muted-foreground">
                {completedSections.length}/{module.sections.length} complete
              </p>
            </div>
          </div>
        </div>
        
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-2">
            {module.sections.map((section, i) => {
              const isComplete = completedSections.includes(section.id);
              const isActive = activeSection?.id === section.id;
              
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                      isComplete 
                        ? "bg-green-500 text-white" 
                        : isActive 
                          ? "bg-primary-foreground text-primary" 
                          : "bg-muted-foreground/20"
                    }`}>
                      {isComplete ? <Check className="w-3 h-3" /> : i + 1}
                    </div>
                    <span className="text-sm font-medium">{section.title}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </ScrollArea>

        <div className="p-4 border-t border-border space-y-2">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate(`/flashcards/${moduleId}`)}
          >
            <BookMarked className="w-4 h-4 mr-2" />
            Study Flashcards
          </Button>
          <Button 
            className="w-full"
            onClick={() => navigate(`/tests/${moduleId}`)}
          >
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Take Practice Test
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {activeSection && (
          <div className="p-6 lg:p-8 max-w-4xl mx-auto">
            {/* Mobile nav */}
            <div className="lg:hidden mb-6">
              <Button variant="ghost" size="sm" onClick={() => navigate("/modules")}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            <div className="space-y-6">
              <div>
                <Badge variant="secondary" className="mb-4">
                  Section {module.sections.findIndex(s => s.id === activeSection.id) + 1} of {module.sections.length}
                </Badge>
                <h1 className="text-3xl font-bold">{activeSection.title}</h1>
              </div>

              <Card>
                <CardContent className="p-6 lg:p-8">
                  <div className="prose prose-invert max-w-none">
                    {activeSection.content.split('\n\n').map((paragraph, i) => {
                      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                        return (
                          <h3 key={i} className="text-lg font-bold text-primary mt-6 mb-3">
                            {paragraph.replace(/\*\*/g, '')}
                          </h3>
                        );
                      }
                      if (paragraph.startsWith('**')) {
                        const parts = paragraph.split('**');
                        return (
                          <p key={i} className="mb-4 leading-relaxed">
                            {parts.map((part, j) => 
                              j % 2 === 1 ? <strong key={j} className="text-primary">{part}</strong> : part
                            )}
                          </p>
                        );
                      }
                      return (
                        <p key={i} className="mb-4 leading-relaxed text-muted-foreground">
                          {paragraph}
                        </p>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Key Terms */}
              {activeSection.key_terms && Object.keys(activeSection.key_terms).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Key Terms</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3">
                      {Object.entries(activeSection.key_terms).map(([term, definition]) => (
                        <div key={term} className="p-4 rounded-lg bg-muted/50">
                          <p className="font-bold text-primary">{term}</p>
                          <p className="text-sm text-muted-foreground mt-1">{definition}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => {
                    const currentIndex = module.sections.findIndex(s => s.id === activeSection.id);
                    if (currentIndex > 0) {
                      setActiveSection(module.sections[currentIndex - 1]);
                    }
                  }}
                  disabled={module.sections.findIndex(s => s.id === activeSection.id) === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>

                {!completedSections.includes(activeSection.id) && (
                  <Button 
                    onClick={() => markSectionComplete(activeSection.id)}
                    className="btn-scale"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Mark Complete
                  </Button>
                )}

                <Button
                  onClick={() => {
                    const currentIndex = module.sections.findIndex(s => s.id === activeSection.id);
                    if (currentIndex < module.sections.length - 1) {
                      setActiveSection(module.sections[currentIndex + 1]);
                    }
                  }}
                  disabled={module.sections.findIndex(s => s.id === activeSection.id) === module.sections.length - 1}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Flashcards Page
function FlashcardsPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/modules/`);
      setModules(response.data);
    } catch (error) {
      toast.error("Failed to load modules");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="flashcards-page">
      <div>
        <h1 className="text-3xl font-bold">Flashcards</h1>
        <p className="text-muted-foreground mt-2">
          Choose a module to study flashcards
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((module) => {
          const IconComponent = iconMap[module.icon] || BookOpen;
          
          return (
            <Card 
              key={module.id}
              className="card-hover cursor-pointer"
              onClick={() => navigate(`/flashcards/${module.id}`)}
              data-testid={`flashcard-module-${module.id}`}
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <IconComponent className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-lg">{module.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Study key terms and concepts
                </p>
                <Button variant="ghost" className="w-full mt-4">
                  Start Studying
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// Flashcard Study Page
function FlashcardStudyPage() {
  const { moduleId } = useParams();
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFlashcards();
  }, [moduleId]);

  const fetchFlashcards = async () => {
    try {
      const response = await axios.get(`${API}/flashcards/${moduleId}`);
      setFlashcards(response.data);
    } catch (error) {
      toast.error("No flashcards found for this module");
      navigate("/flashcards");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentIndex < flashcards.length - 1) {
      setIsFlipped(false);
      setTimeout(() => setCurrentIndex(currentIndex + 1), 150);
    }
    // Update progress
    try {
      await axios.post(`${API}/progress/update`, {
        module_id: moduleId,
        flashcards_reviewed: 1,
      });
    } catch (error) {
      // Silently fail
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setIsFlipped(false);
      setTimeout(() => setCurrentIndex(currentIndex - 1), 150);
    }
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading flashcards...</div>
      </div>
    );
  }

  const currentCard = flashcards[currentIndex];

  return (
    <div className="p-6 lg:p-8" data-testid="flashcard-study-page">
      <Button variant="ghost" onClick={() => navigate("/flashcards")} className="mb-6">
        <ChevronLeft className="w-4 h-4 mr-2" />
        Back to Flashcards
      </Button>

      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold capitalize">
            {moduleId.replace(/-/g, " ")} Flashcards
          </h1>
          <p className="text-muted-foreground mt-2">
            Card {currentIndex + 1} of {flashcards.length}
          </p>
          <Progress value={((currentIndex + 1) / flashcards.length) * 100} className="mt-4 h-2" />
        </div>

        {/* Flashcard */}
        <Card 
          className="h-80 flex items-center justify-center p-8 cursor-pointer transition-all duration-300 hover:border-primary/50"
          onClick={() => setIsFlipped(!isFlipped)}
          data-testid="flashcard"
        >
          {!isFlipped ? (
            <div className="text-center animate-fadeIn">
              <Badge variant="secondary" className="mb-4">{currentCard?.category}</Badge>
              <p className="text-xl font-medium">{currentCard?.front}</p>
              <p className="text-sm text-muted-foreground mt-6">Click to reveal answer</p>
            </div>
          ) : (
            <div className="text-center animate-fadeIn">
              <Badge className="mb-4">Answer</Badge>
              <p className="text-lg">{currentCard?.back}</p>
              <p className="text-sm text-muted-foreground mt-6">Click to see question</p>
            </div>
          )}
        </Card>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          <Button 
            variant="outline" 
            onClick={handlePrev}
            disabled={currentIndex === 0}
            data-testid="flashcard-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          <Button 
            variant="outline"
            onClick={handleReset}
            data-testid="flashcard-reset"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button 
            onClick={handleNext}
            disabled={currentIndex === flashcards.length - 1}
            data-testid="flashcard-next"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Practice Tests Page
function PracticeTestsPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/modules/`);
      setModules(response.data);
    } catch (error) {
      toast.error("Failed to load modules");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="practice-tests-page">
      <div>
        <h1 className="text-3xl font-bold">Practice Tests</h1>
        <p className="text-muted-foreground mt-2">
          Test your knowledge with practice quizzes
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((module) => {
          const IconComponent = iconMap[module.icon] || BookOpen;
          
          return (
            <Card 
              key={module.id}
              className="card-hover cursor-pointer"
              onClick={() => navigate(`/tests/${module.id}`)}
              data-testid={`test-module-${module.id}`}
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <IconComponent className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-lg">{module.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {module.test_areas.join(", ")}
                </p>
                <Button variant="ghost" className="w-full mt-4">
                  Start Test
                  <Play className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// Take Test Page
function TakeTestPage() {
  const { testType } = useParams();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [correctAnswers, setCorrectAnswers] = useState({});
  const [coaching, setCoaching] = useState(null);
  const [coachingLoading, setCoachingLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuestions();
  }, [testType]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/tests/questions/${testType}?count=15`);
      setQuestions(response.data);
    } catch (error) {
      toast.error("No questions found for this test");
      navigate("/tests");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answerIndex) => {
    setAnswers({ ...answers, [questionId]: answerIndex });
  };

  const generateCoaching = async (missedQuestions, score, total) => {
    if (missedQuestions.length === 0) return;
    
    setCoachingLoading(true);
    try {
      const response = await axios.post(`${API}/coaching/generate`, {
        test_type: testType,
        missed_questions: missedQuestions,
        score: score,
        total: total
      });
      setCoaching(response.data.coaching);
    } catch (error) {
      console.error("Failed to generate coaching:", error);
      // Silently fail - coaching is optional
    } finally {
      setCoachingLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(`${API}/tests/submit`, {
        test_type: testType,
        answers: answers,
      });
      setResults(response.data);
      
      // Fetch correct answers
      const answersRes = await axios.get(`${API}/tests/answers/${testType}`);
      const answersMap = {};
      answersRes.data.forEach(a => {
        answersMap[a.id] = { correct: a.correct, explanation: a.explanation };
      });
      setCorrectAnswers(answersMap);
      
      // Build missed questions for coaching
      const missedQuestions = [];
      questions.forEach(q => {
        const userAnswer = answers[q.id];
        const correctData = answersRes.data.find(a => a.id === q.id);
        if (correctData && userAnswer !== correctData.correct) {
          missedQuestions.push({
            question: q.question,
            user_answer: q.options[userAnswer] || "No answer",
            correct_answer: q.options[correctData.correct],
            explanation: correctData.explanation
          });
        }
      });
      
      setShowResults(true);
      
      // Generate coaching if there are missed questions
      if (missedQuestions.length > 0) {
        generateCoaching(missedQuestions, response.data.score, response.data.total);
      }
    } catch (error) {
      toast.error("Failed to submit test");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading test...</div>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="test-results">
        <Card className="mb-6">
          <CardHeader className="text-center">
            <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4 ${
              results.passed ? 'bg-green-500/20' : 'bg-destructive/20'
            }`}>
              {results.passed ? (
                <Award className="w-10 h-10 text-green-500" />
              ) : (
                <X className="w-10 h-10 text-destructive" />
              )}
            </div>
            <CardTitle className="text-3xl">
              {results.passed ? "Congratulations!" : "Keep Practicing"}
            </CardTitle>
            <CardDescription>
              You scored {results.score} out of {results.total} ({results.percentage}%)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={results.percentage} className="h-3 mb-6" />
            {results.weak_areas.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="font-medium mb-2">Areas to Review:</p>
                <div className="flex flex-wrap gap-2">
                  {results.weak_areas.map((area, i) => (
                    <Badge key={i} variant="secondary">{area}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex gap-4 justify-center">
            <Button variant="outline" onClick={() => navigate("/tests")}>
              Back to Tests
            </Button>
            <Button onClick={() => {
              setShowResults(false);
              setAnswers({});
              setCurrentIndex(0);
              setCoaching(null);
              fetchQuestions();
            }}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Retry Test
            </Button>
          </CardFooter>
        </Card>

        {/* AI Coaching Section */}
        {(coachingLoading || coaching) && (
          <Card className="mb-6 border-primary/30 bg-primary/5">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">AI Coach Feedback</CardTitle>
                  <CardDescription>Personalized guidance based on your results</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {coachingLoading ? (
                <div className="flex items-center gap-3 py-8 justify-center">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span className="text-muted-foreground">Generating personalized coaching...</span>
                </div>
              ) : coaching ? (
                <div className="prose prose-invert max-w-none">
                  {coaching.split('\n').map((line, i) => {
                    if (line.startsWith('##')) {
                      return <h3 key={i} className="text-lg font-bold text-primary mt-4 mb-2">{line.replace(/^#+\s*/, '')}</h3>;
                    }
                    if (line.startsWith('#')) {
                      return <h2 key={i} className="text-xl font-bold mt-4 mb-2">{line.replace(/^#+\s*/, '')}</h2>;
                    }
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return <p key={i} className="font-bold text-primary mt-3 mb-1">{line.replace(/\*\*/g, '')}</p>;
                    }
                    if (line.startsWith('- ') || line.startsWith('• ')) {
                      return <p key={i} className="ml-4 text-muted-foreground">• {line.substring(2)}</p>;
                    }
                    if (line.match(/^\d+\./)) {
                      return <p key={i} className="ml-4 text-muted-foreground">{line}</p>;
                    }
                    if (line.trim() === '') {
                      return <div key={i} className="h-2" />;
                    }
                    return <p key={i} className="text-muted-foreground leading-relaxed">{line}</p>;
                  })}
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Review Questions */}
        <h2 className="text-xl font-bold mb-4">Review Your Answers</h2>
        <div className="space-y-4">
          {questions.map((q, i) => {
            const userAnswer = answers[q.id];
            const correct = correctAnswers[q.id]?.correct;
            const isCorrect = userAnswer === correct;

            return (
              <Card key={q.id} className={isCorrect ? 'border-green-500/50' : 'border-destructive/50'}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      isCorrect ? 'bg-green-500' : 'bg-destructive'
                    }`}>
                      {isCorrect ? <Check className="w-4 h-4 text-white" /> : <X className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium mb-2">{i + 1}. {q.question}</p>
                      <div className="space-y-1 text-sm">
                        {q.options.map((option, oi) => (
                          <p key={oi} className={`p-2 rounded ${
                            oi === correct ? 'bg-green-500/20 text-green-400' :
                            oi === userAnswer && oi !== correct ? 'bg-destructive/20 text-destructive' :
                            'text-muted-foreground'
                          }`}>
                            {String.fromCharCode(65 + oi)}. {option}
                          </p>
                        ))}
                      </div>
                      {correctAnswers[q.id]?.explanation && (
                        <p className="text-sm text-muted-foreground mt-2 italic">
                          {correctAnswers[q.id].explanation}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="take-test-page">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate("/tests")}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Exit Test
        </Button>
        <Badge variant="secondary">
          {answeredCount}/{questions.length} Answered
        </Badge>
      </div>

      <Progress value={(currentIndex / questions.length) * 100} className="mb-8 h-2" />

      <Card>
        <CardHeader>
          <CardDescription>Question {currentIndex + 1} of {questions.length}</CardDescription>
          <CardTitle className="text-xl">{currentQuestion?.question}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {currentQuestion?.options.map((option, i) => (
              <button
                key={i}
                onClick={() => handleAnswer(currentQuestion.id, i)}
                className={`w-full text-left p-4 rounded-lg border transition-colors ${
                  answers[currentQuestion.id] === i
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid={`option-${i}`}
              >
                <span className="font-medium mr-3">{String.fromCharCode(65 + i)}.</span>
                {option}
              </button>
            ))}
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentIndex === questions.length - 1 ? (
            <Button 
              onClick={handleSubmit}
              disabled={answeredCount < questions.length}
              data-testid="submit-test"
            >
              Submit Test
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentIndex(Math.min(questions.length - 1, currentIndex + 1))}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </CardFooter>
      </Card>

      {/* Question Navigation */}
      <div className="mt-6">
        <p className="text-sm text-muted-foreground mb-3">Jump to Question:</p>
        <div className="flex flex-wrap gap-2">
          {questions.map((q, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                currentIndex === i
                  ? 'bg-primary text-primary-foreground'
                  : answers[q.id] !== undefined
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-muted hover:bg-muted-foreground/20'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Exam Simulation Page
function ExamSimulationPage() {
  const [exam, setExam] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(90 * 60); // 90 minutes in seconds
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [coaching, setCoaching] = useState(null);
  const [coachingLoading, setCoachingLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchExam();
  }, []);

  useEffect(() => {
    let timer;
    if (started && timeLeft > 0 && !showResults) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [started, timeLeft, showResults]);

  const fetchExam = async () => {
    try {
      const response = await axios.get(`${API}/tests/exam/full`);
      setExam(response.data);
    } catch (error) {
      toast.error("Failed to load exam");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answerIndex) => {
    setAnswers({ ...answers, [questionId]: answerIndex });
  };

  const generateCoaching = async (missedQuestions, score, total) => {
    if (missedQuestions.length === 0) return;
    
    setCoachingLoading(true);
    try {
      const response = await axios.post(`${API}/coaching/generate`, {
        test_type: "full-exam",
        missed_questions: missedQuestions,
        score: score,
        total: total
      });
      setCoaching(response.data.coaching);
    } catch (error) {
      console.error("Failed to generate coaching:", error);
    } finally {
      setCoachingLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(`${API}/tests/submit`, {
        test_type: "full-exam",
        answers: answers,
      });
      setResults(response.data);
      setShowResults(true);
      
      // Build missed questions for coaching (we don't have correct answers for exam, so simplified)
      const missedCount = response.data.total - response.data.score;
      if (missedCount > 0 && response.data.weak_areas?.length > 0) {
        // Generate coaching based on weak areas
        const missedQuestions = response.data.weak_areas.map(area => ({
          question: `Questions in ${area} category`,
          user_answer: "Incorrect",
          correct_answer: "See study materials",
          explanation: `Review the ${area} module for better understanding.`
        }));
        generateCoaching(missedQuestions, response.data.score, response.data.total);
      }
    } catch (error) {
      toast.error("Failed to submit exam");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading exam...</div>
      </div>
    );
  }

  if (!started) {
    return (
      <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="exam-start">
        <Card>
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-primary/20 mx-auto flex items-center justify-center mb-4">
              <GraduationCap className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">{exam?.title}</CardTitle>
            <CardDescription className="text-base">
              Texas CDL Knowledge Evaluation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-4 rounded-lg bg-muted">
                <p className="text-2xl font-bold text-primary">{exam?.questions.length}</p>
                <p className="text-sm text-muted-foreground">Questions</p>
              </div>
              <div className="p-4 rounded-lg bg-muted">
                <p className="text-2xl font-bold text-primary">{exam?.time_limit} min</p>
                <p className="text-sm text-muted-foreground">Time Limit</p>
              </div>
            </div>
            
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-200">
                <strong>Note:</strong> {exam?.disclaimer}
              </p>
            </div>

            <div className="space-y-2 text-sm text-muted-foreground">
              <p>• You need {exam?.passing_score}% to pass</p>
              <p>• Once started, the timer cannot be paused</p>
              <p>• Review your answers before submitting</p>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full h-12 text-lg btn-scale" 
              onClick={() => setStarted(true)}
              data-testid="start-exam-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              Start Exam
            </Button>
          </CardFooter>
        </Card>
        
        <p className="text-xs text-muted-foreground text-center mt-6">
          Training material by Gideon Learning, operated by Mesa Winds Intelligence, LLC — A Holcomb Company
        </p>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="exam-results">
        <Card className="mb-6">
          <CardHeader className="text-center">
            <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4 ${
              results?.passed ? 'bg-green-500/20' : 'bg-destructive/20'
            }`}>
              {results?.passed ? (
                <Award className="w-10 h-10 text-green-500" />
              ) : (
                <X className="w-10 h-10 text-destructive" />
              )}
            </div>
            <CardTitle className="text-3xl">
              {results?.passed ? "You Passed!" : "Not Quite There Yet"}
            </CardTitle>
            <CardDescription className="text-lg">
              Score: {results?.score}/{results?.total} ({results?.percentage}%)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={results?.percentage} className="h-4 mb-6" />
            
            {results?.weak_areas?.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-4 mb-4">
                <p className="font-medium mb-2">Areas Needing Review:</p>
                <div className="flex flex-wrap gap-2">
                  {results.weak_areas.map((area, i) => (
                    <Badge key={i} variant="secondary">{area}</Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center py-4">
              <p className={`text-lg font-medium ${results?.passed ? 'text-green-400' : 'text-amber-400'}`}>
                {results?.passed 
                  ? "Great job! You're ready for the real exam."
                  : `Keep studying! You need ${exam?.passing_score}% to pass.`
                }
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex gap-4 justify-center">
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </Button>
            <Button onClick={() => {
              setShowResults(false);
              setStarted(false);
              setAnswers({});
              setCurrentIndex(0);
              setTimeLeft(90 * 60);
              setCoaching(null);
              fetchExam();
            }}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Retry Exam
            </Button>
          </CardFooter>
        </Card>

        {/* AI Coaching Section */}
        {(coachingLoading || coaching) && (
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">AI Coach Feedback</CardTitle>
                  <CardDescription>Personalized guidance based on your exam</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {coachingLoading ? (
                <div className="flex items-center gap-3 py-8 justify-center">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span className="text-muted-foreground">Generating personalized coaching...</span>
                </div>
              ) : coaching ? (
                <div className="prose prose-invert max-w-none">
                  {coaching.split('\n').map((line, i) => {
                    if (line.startsWith('##')) {
                      return <h3 key={i} className="text-lg font-bold text-primary mt-4 mb-2">{line.replace(/^#+\s*/, '')}</h3>;
                    }
                    if (line.startsWith('#')) {
                      return <h2 key={i} className="text-xl font-bold mt-4 mb-2">{line.replace(/^#+\s*/, '')}</h2>;
                    }
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return <p key={i} className="font-bold text-primary mt-3 mb-1">{line.replace(/\*\*/g, '')}</p>;
                    }
                    if (line.startsWith('- ') || line.startsWith('• ')) {
                      return <p key={i} className="ml-4 text-muted-foreground">• {line.substring(2)}</p>;
                    }
                    if (line.match(/^\d+\./)) {
                      return <p key={i} className="ml-4 text-muted-foreground">{line}</p>;
                    }
                    if (line.trim() === '') {
                      return <div key={i} className="h-2" />;
                    }
                    return <p key={i} className="text-muted-foreground leading-relaxed">{line}</p>;
                  })}
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  const currentQuestion = exam?.questions[currentIndex];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="exam-simulation-page">
      {/* Timer */}
      <div className="fixed top-4 right-4 z-50">
        <Card className={`${timeLeft < 300 ? 'border-destructive animate-pulse' : ''}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className={`w-5 h-5 ${timeLeft < 300 ? 'text-destructive' : 'text-primary'}`} />
            <span className={`text-xl font-mono font-bold ${timeLeft < 300 ? 'text-destructive' : ''}`}>
              {formatTime(timeLeft)}
            </span>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold">CDL Knowledge Evaluation</h1>
        <Badge variant="secondary">
          {answeredCount}/{exam?.questions.length} Answered
        </Badge>
      </div>

      <Progress value={(currentIndex / exam?.questions.length) * 100} className="mb-8 h-2" />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardDescription>Question {currentIndex + 1} of {exam?.questions.length}</CardDescription>
            <Badge variant="outline">{currentQuestion?.category}</Badge>
          </div>
          <CardTitle className="text-xl">{currentQuestion?.question}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {currentQuestion?.options.map((option, i) => (
              <button
                key={i}
                onClick={() => handleAnswer(currentQuestion.id, i)}
                className={`w-full text-left p-4 rounded-lg border transition-colors ${
                  answers[currentQuestion.id] === i
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <span className="font-medium mr-3">{String.fromCharCode(65 + i)}.</span>
                {option}
              </button>
            ))}
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentIndex === exam?.questions.length - 1 ? (
            <Dialog>
              <Button 
                disabled={answeredCount < exam?.questions.length}
                onClick={handleSubmit}
              >
                Submit Exam
              </Button>
            </Dialog>
          ) : (
            <Button
              onClick={() => setCurrentIndex(Math.min(exam?.questions.length - 1, currentIndex + 1))}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </CardFooter>
      </Card>

      {/* Question Navigation */}
      <div className="mt-6">
        <p className="text-sm text-muted-foreground mb-3">Question Navigator:</p>
        <div className="flex flex-wrap gap-2">
          {exam?.questions.map((q, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                currentIndex === i
                  ? 'bg-primary text-primary-foreground'
                  : answers[q.id] !== undefined
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-muted hover:bg-muted-foreground/20'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Progress Page
function ProgressPage() {
  const [progress, setProgress] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const [progressRes, testsRes, modulesRes] = await Promise.all([
        axios.get(`${API}/progress/`),
        axios.get(`${API}/tests/results`),
        axios.get(`${API}/modules/`),
      ]);
      setProgress(progressRes.data);
      setTestResults(testsRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      toast.error("Failed to load progress");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading progress...</div>
      </div>
    );
  }

  const totalSections = modules.reduce((acc, m) => acc + m.sections.length, 0);
  const completedSections = Object.values(progress?.sections_completed || {}).flat().length;
  const passedTests = testResults.filter(t => t.passed).length;
  const avgScore = testResults.length > 0 
    ? (testResults.reduce((acc, t) => acc + t.percentage, 0) / testResults.length).toFixed(1)
    : 0;

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="progress-page">
      <div>
        <h1 className="text-3xl font-bold">Your Progress</h1>
        <p className="text-muted-foreground mt-2">
          Track your learning journey
        </p>
      </div>

      {/* Overview Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardHeader>
            <CardDescription>Overall Progress</CardDescription>
            <CardTitle className="text-4xl text-primary">
              {progress?.overall_progress?.toFixed(0) || 0}%
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={progress?.overall_progress || 0} className="h-2" />
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardHeader>
            <CardDescription>Sections Completed</CardDescription>
            <CardTitle className="text-4xl">
              {completedSections}<span className="text-xl text-muted-foreground">/{totalSections}</span>
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="card-hover">
          <CardHeader>
            <CardDescription>Tests Passed</CardDescription>
            <CardTitle className="text-4xl">
              {passedTests}<span className="text-xl text-muted-foreground">/{testResults.length}</span>
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="card-hover">
          <CardHeader>
            <CardDescription>Average Score</CardDescription>
            <CardTitle className="text-4xl">{avgScore}%</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Module Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Module Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {modules.map((module) => {
              const IconComponent = iconMap[module.icon] || BookOpen;
              const completed = progress?.sections_completed?.[module.id]?.length || 0;
              const total = module.sections.length;
              const percent = (completed / total) * 100;

              return (
                <div key={module.id} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <IconComponent className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium truncate">{module.title}</p>
                      <span className="text-sm text-muted-foreground">
                        {completed}/{total}
                      </span>
                    </div>
                    <Progress value={percent} className="h-2" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Test History */}
      <Card>
        <CardHeader>
          <CardTitle>Test History</CardTitle>
        </CardHeader>
        <CardContent>
          {testResults.length > 0 ? (
            <div className="space-y-3">
              {testResults.map((test, i) => (
                <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium capitalize">
                      {test.test_type.replace(/-/g, " ")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(test.completed_at).toLocaleDateString()} at{" "}
                      {new Date(test.completed_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-lg font-bold">
                      {test.score}/{test.total}
                    </p>
                    <Badge variant={test.passed ? "default" : "destructive"}>
                      {test.percentage}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              No tests taken yet
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Settings Page
function SettingsPage() {
  const { user, logout } = useAuth();
  const { theme, setTheme, themes: themeOptions } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-2">
          Manage your account and preferences
        </p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <p className="text-xl font-bold">{user?.first_name} {user?.last_name}</p>
              <p className="text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Theme */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>Choose your preferred theme</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {themeOptions.map((t) => (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                className={`p-4 rounded-lg border-2 transition-colors text-left ${
                  theme === t.id 
                    ? 'border-primary bg-primary/10' 
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid={`theme-${t.id}`}
              >
                <t.icon className="w-6 h-6 text-primary mb-2" />
                <p className="font-medium">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Logout */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle>Account Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <Button 
            variant="destructive" 
            onClick={() => {
              logout();
              navigate("/");
            }}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Admin Panel Page (Admin Only)
function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/dashboard");
      return;
    }
    fetchUsers();
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const toggleAdmin = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/admin?is_admin=${!currentStatus}`);
      toast.success("Admin status updated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update admin status");
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  if (!user?.is_admin) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="admin-panel">
      <div>
        <h1 className="text-3xl font-bold">Admin Panel</h1>
        <p className="text-muted-foreground mt-2">Manage users and system settings</p>
      </div>

      <div className="flex gap-4">
        <Button onClick={() => navigate("/admin/metrics")} variant="outline">
          <BarChart3 className="w-4 h-4 mr-2" />
          View Metrics
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users ({users.length})</CardTitle>
          <CardDescription>Manage user accounts and permissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{u.first_name} {u.last_name}</p>
                    <p className="text-sm text-muted-foreground">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {u.is_admin && <Badge>Admin</Badge>}
                  <Button
                    size="sm"
                    variant={u.is_admin ? "secondary" : "outline"}
                    onClick={() => toggleAdmin(u.id, u.is_admin)}
                    disabled={u.email === user.email}
                  >
                    {u.is_admin ? "Remove Admin" : "Make Admin"}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => deleteUser(u.id, u.email)}
                    disabled={u.email === user.email}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Metrics Page (Admin Only)
function MetricsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState([]);
  const [moduleStats, setModuleStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/dashboard");
      return;
    }
    fetchMetrics();
  }, [user]);

  const fetchMetrics = async () => {
    try {
      const [dashRes, usersRes, modulesRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/analytics/users`),
        axios.get(`${API}/analytics/module-stats`),
      ]);
      setDashboard(dashRes.data);
      setUserAnalytics(usersRes.data);
      setModuleStats(modulesRes.data);
    } catch (error) {
      toast.error("Failed to load metrics");
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0m";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  if (!user?.is_admin) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading metrics...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="metrics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Analytics & Metrics</h1>
          <p className="text-muted-foreground mt-2">Track user engagement and progress</p>
        </div>
        <Button onClick={() => navigate("/admin")} variant="outline">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Admin
        </Button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Users</CardDescription>
            <CardTitle className="text-3xl text-primary">{dashboard?.total_users || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active (7 days)</CardDescription>
            <CardTitle className="text-3xl">{dashboard?.active_users_7d || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Tests Taken</CardDescription>
            <CardTitle className="text-3xl">{dashboard?.total_tests || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Score</CardDescription>
            <CardTitle className="text-3xl">{dashboard?.average_score || 0}%</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* User Analytics Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Analytics</CardTitle>
          <CardDescription>Detailed user engagement metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-medium">User</th>
                  <th className="text-left p-3 font-medium">Last IP</th>
                  <th className="text-left p-3 font-medium">Last Active</th>
                  <th className="text-left p-3 font-medium">Time Spent</th>
                  <th className="text-left p-3 font-medium">Tests</th>
                  <th className="text-left p-3 font-medium">Avg Score</th>
                  <th className="text-left p-3 font-medium">Progress</th>
                </tr>
              </thead>
              <tbody>
                {userAnalytics.map((u) => (
                  <tr key={u.user_id} className="border-b border-border/50 hover:bg-muted/50">
                    <td className="p-3">
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs">{u.last_ip || "—"}</td>
                    <td className="p-3 text-xs">
                      {u.last_activity 
                        ? new Date(u.last_activity).toLocaleString() 
                        : "Never"}
                    </td>
                    <td className="p-3">{formatDuration(u.total_time_seconds)}</td>
                    <td className="p-3">
                      {u.tests_passed}/{u.tests_taken}
                      {u.tests_taken > 0 && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({Math.round((u.tests_passed / u.tests_taken) * 100)}%)
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge variant={u.average_score >= 80 ? "default" : "secondary"}>
                        {u.average_score}%
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Progress value={u.overall_progress} className="h-2 w-20" />
                        <span className="text-xs">{u.overall_progress.toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Time Per Module */}
      <Card>
        <CardHeader>
          <CardTitle>Time Spent by Module</CardTitle>
          <CardDescription>Breakdown of time users spent on each module</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {userAnalytics.filter(u => Object.keys(u.time_per_module_seconds || {}).length > 0).map((u) => (
              <div key={u.user_id} className="p-4 rounded-lg bg-muted/50">
                <p className="font-medium mb-2">{u.name}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {Object.entries(u.time_per_module_seconds || {}).map(([moduleId, seconds]) => (
                    <div key={moduleId} className="text-xs p-2 rounded bg-background">
                      <p className="font-medium capitalize">{moduleId.replace(/-/g, " ")}</p>
                      <p className="text-muted-foreground">{formatDuration(seconds)}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {userAnalytics.filter(u => Object.keys(u.time_per_module_seconds || {}).length > 0).length === 0 && (
              <p className="text-muted-foreground text-center py-8">No module activity tracked yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Main App
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/course/:courseId"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CourseDashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/modules"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ModulesPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/modules/:moduleId"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ModuleDetailPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/flashcards"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <FlashcardsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/flashcards/:moduleId"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <FlashcardStudyPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PracticeTestsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tests/:testType"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TakeTestPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/exam"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ExamSimulationPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/progress"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProgressPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <SettingsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <AdminPanel />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/metrics"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <MetricsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
